import type { Field } from "@fobu/schema";

const norm = (s: string) => s.trim().toLocaleLowerCase("de");

function matchChoice(choices: Array<{ id: string; label: string }>, raw: string): string | undefined {
  const v = norm(raw);
  return choices.find((c) => norm(c.id) === v || norm(c.label) === v)?.id;
}

/** „zeile:spalte,zeile2:spalte2“ → Paare (Terminumfrage, Matrix). */
const pairs = (raw: string) =>
  raw
    .split(",")
    .map((p) => p.split(":"))
    .filter((p): p is [string, string] => p.length === 2 && !!p[0] && !!p[1]);

/**
 * Wert aus einem URL-Parameter (Vorbelegung, Antwortknöpfe der Einladungsmail) für ein Feld lesen.
 * `undefined` = nicht vorbelegbar oder ungültig.
 */
export function parseUrlValue(field: Field, raw: string): unknown {
  switch (field.type) {
    case "number":
    case "rating":
    case "nps":
    case "scale":
    case "slider": {
      const n = Number(raw.replace(",", "."));
      return Number.isFinite(n) ? n : undefined;
    }
    case "yes_no":
      return ["1", "true", "ja", "yes"].includes(norm(raw)) ? true : ["0", "false", "nein", "no"].includes(norm(raw)) ? false : undefined;
    case "single_choice":
    case "dropdown":
      return matchChoice(field.choices, raw);
    case "multiple_choice": {
      const ids = raw.split(",").map((p) => matchChoice(field.choices, p)).filter((x): x is string => !!x);
      return ids.length ? ids : undefined;
    }
    case "poll": {
      const ids = new Set(field.options.map((o) => o.id));
      const out: Record<string, string> = {};
      for (const [id, vote] of pairs(raw)) if (ids.has(id) && (vote === "yes" || vote === "no" || (vote === "maybe" && field.allowMaybe))) out[id] = vote;
      return Object.keys(out).length ? out : undefined;
    }
    case "matrix": {
      const out: Record<string, string | string[]> = {};
      for (const [r, c] of pairs(raw)) {
        const row = matchChoice(field.rows, r);
        const col = matchChoice(field.columns, c);
        if (row && col) out[row] = field.multiple ? [...((out[row] as string[] | undefined) ?? []), col] : col;
      }
      return Object.keys(out).length ? out : undefined;
    }
    case "short_text":
    case "long_text":
    case "email":
    case "phone":
    case "url":
    case "date":
    case "time":
    case "datetime":
      return raw.slice(0, 10_000);
    default:
      // Einwilligung, Upload, Unterschrift, Reihenfolge u. a.: bewusst nicht per URL vorbelegbar.
      return undefined;
  }
}
